import React, { Component } from 'react';
import { Box, IconButton, CircularProgress, Typography, Button } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import CrownIcon from 'assets/img/icons/crown.svg';
import mammoth from 'mammoth';
import { Document, Page, pdfjs } from 'react-pdf';

// Configure PDF.js worker for react-pdf 5.x
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const styles = theme => ({
  viewerContainer: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#fff',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    height: '100%',
    width: '100%',
    opacity: 1,
    overflow: 'hidden',
    borderRadius: 12,
  },
  viewerOpen: {
    width: '100%',
    opacity: 1,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '12px 16px',
    backgroundColor: '#fff',
    borderRadius: '8px 8px 0 0',
    borderBottom: '1px solid #e0e0e0',
  },
  headerLeft: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  headerTitleRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 4,
  },
  crownIcon: {
    width: 24,
    height: 24,
    marginRight: 8,
  },
  headerTitle: {
    fontWeight: 'bold',
    fontSize: 16,
    color: '#2A2A2A',
    whiteSpace: 'nowrap',
  },
  headerDisclaimer: {
    fontSize: 11,
    color: '#3B3B3B',
    fontWeight: '600',
    marginLeft: 8,
  },
  headerNote: {
    fontSize: 10,
    color: '#5E5E5E',
    lineHeight: 1.4,
    marginTop: 4,
    fontStyle: 'italic',
  },
  closeButton: {
    color: '#666',
    backgroundColor: 'transparent',
    padding: 6,
    marginLeft: 8,
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.05)',
    },
  },
  contentArea: {
    flex: 1,
    overflow: 'auto',
    padding: '16px',
    backgroundColor: '#fff',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
  },
  errorContainer: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: '#f44336',
  },
  disclaimer: {
    backgroundColor: '#FFFBEB',
    border: '1px solid #FCD34D',
    borderRadius: 8,
    padding: '14px 16px',
    marginBottom: 16,
    fontSize: 13,
    color: '#92400E',
    lineHeight: 1.5,
  },
  htmlContent: {
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 16,
    '& table': {
      width: '100%',
      borderCollapse: 'collapse',
      marginTop: 8,
    },
    '& th, & td': {
      border: '1px solid #ccc',
      padding: '6px 8px',
      textAlign: 'left',
      verticalAlign: 'top',
    },
    '& th': {
      backgroundColor: '#e6eef7',
      fontWeight: 'bold',
    },
  },
  htmlIframe: {
    width: '100%',
    height: '100%',
    minHeight: '500px',
    border: 'none',
    backgroundColor: '#fff',
    borderRadius: 8,
  },
  pdfEmbed: {
    width: '100%',
    height: 'calc(100% - 100px)',
    border: 'none',
  },
  pdfContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  pdfPageControls: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px',
    marginTop: '16px',
    marginBottom: '16px',
  },
  imageContainer: {
    textAlign: 'center',
    '& img': {
      maxWidth: '100%',
      height: 'auto',
      border: '1px solid #e0e0e0',
      borderRadius: 4,
    },
  },
});

class ContentViewerPanel extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      error: null,
      convertedHtml: '', // For DOCX conversion
      numPages: null, // For PDF pagination
      pageNumber: 1, // Current PDF page
    };
  }

  componentDidUpdate(prevProps) {
    // If content type changed to DOCX and we have content, convert it
    if (
      this.props.contentType === 'docx' &&
      this.props.content &&
      this.props.content !== prevProps.content
    ) {
      this.convertDocxToHtml(this.props.content);
    }

    // Reset page number when PDF content changes
    if (
      this.props.contentType === 'pdf' &&
      this.props.content !== prevProps.content
    ) {
      this.setState({ pageNumber: 1, numPages: null });
    }
  }

  /**
   * PDF document loaded successfully
   */
  onDocumentLoadSuccess = ({ numPages }) => {
    this.setState({ numPages, loading: false, error: null });
  };

  /**
   * PDF document load error
   */
  onDocumentLoadError = (error) => {
    console.error('Error loading PDF:', error);
    this.setState({
      loading: false,
      error: 'Failed to load PDF document. Please try downloading instead.',
    });
  };

  /**
   * Change PDF page
   */
  changePage = (offset) => {
    this.setState(prevState => ({
      pageNumber: prevState.pageNumber + offset,
    }));
  };

  /**
   * Go to previous PDF page
   */
  previousPage = () => {
    this.changePage(-1);
  };

  /**
   * Go to next PDF page
   */
  nextPage = () => {
    this.changePage(1);
  };

  /**
   * Convert DOCX to HTML using mammoth.js
   */
  convertDocxToHtml = async (base64Content) => {
    this.setState({ loading: true, error: null });

    try {
      // Convert base64 to array buffer
      const binaryString = atob(base64Content.split(',')[1] || base64Content);
      const len = binaryString.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const arrayBuffer = bytes.buffer;

      // Convert using mammoth
      const result = await mammoth.convertToHtml({ arrayBuffer: arrayBuffer });
      this.setState({
        convertedHtml: result.value,
        loading: false,
      });

      // Log any warnings
      if (result.messages && result.messages.length > 0) {
        console.warn('DOCX conversion warnings:', result.messages);
      }
    } catch (error) {
      console.error('Error converting DOCX:', error);
      this.setState({
        loading: false,
        error: 'Failed to convert document. Please try downloading instead.',
      });
    }
  };

  /**
   * Render content based on type
   */
  renderContent = () => {
    const { classes, contentType, content, fileName } = this.props;
    const { loading, error, convertedHtml } = this.state;

    if (loading) {
      return (
        <Box className={classes.loadingContainer}>
          <CircularProgress size={60} style={{ color: '#51C1C0' }} />
        </Box>
      );
    }

    if (error) {
      return (
        <Box className={classes.errorContainer}>
          <Typography variant="h6">Error Loading Document</Typography>
          <Typography variant="body2" style={{ marginTop: 8 }}>
            {error}
          </Typography>
        </Box>
      );
    }

    switch (contentType) {
      case 'html':
        // Use iframe with srcdoc to isolate HTML content and prevent body margin issues
        const wrappedHtml = `
          <!DOCTYPE html>
          <html>
          <head>
            <style>
              body { margin: 0 !important; padding: 16px; font-family: Arial, sans-serif; background-color: #fff; }
              table { width: 100%; border-collapse: collapse; margin-top: 8px; }
              th, td { border: 1px solid #ccc; padding: 6px 8px; text-align: left; vertical-align: top; }
              th { background-color: #e6eef7; font-weight: bold; }
            </style>
          </head>
          <body>${content}</body>
          </html>
        `;
        return (
          <iframe
            className={classes.htmlIframe}
            srcDoc={wrappedHtml}
            title="AI Referral Insights"
            sandbox="allow-same-origin"
          />
        );

      case 'pdf':
        const { pageNumber, numPages } = this.state;
        return (
          <Box className={classes.pdfContainer}>
            <Document
              file={content}
              onLoadSuccess={this.onDocumentLoadSuccess}
              onLoadError={this.onDocumentLoadError}
              loading={
                <Box className={classes.loadingContainer}>
                  <CircularProgress size={60} style={{ color: '#51C1C0' }} />
                </Box>
              }
            >
              <Page
                pageNumber={pageNumber}
                width={600}
              />
            </Document>

            {numPages && (
              <Box className={classes.pdfPageControls}>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={pageNumber <= 1}
                  onClick={this.previousPage}
                  style={{ textTransform: 'none' }}
                >
                  Previous
                </Button>
                <Typography variant="body2" style={{ color: '#666' }}>
                  Page {pageNumber} of {numPages}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={pageNumber >= numPages}
                  onClick={this.nextPage}
                  style={{ textTransform: 'none' }}
                >
                  Next
                </Button>
              </Box>
            )}
          </Box>
        );

      case 'image':
        return (
          <Box className={classes.imageContainer}>
            <img src={content} alt={fileName || 'Document Image'} />
          </Box>
        );

      case 'docx':
        return (
          <>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Document: {fileName}
            </Typography>
            <Box
              className={classes.htmlContent}
              dangerouslySetInnerHTML={{ __html: convertedHtml }}
            />
          </>
        );

      default:
        return (
          <Box className={classes.errorContainer}>
            <Typography variant="h6">Unsupported File Type</Typography>
            <Typography variant="body2" style={{ marginTop: 8 }}>
              This file type cannot be previewed. Please download to view.
            </Typography>
          </Box>
        );
    }
  };

  render() {
    const { classes, open, onClose, fileName, contentType } = this.props;

    return (
      <Box className={`${classes.viewerContainer} ${open ? classes.viewerOpen : ''} content-viewer-overlay-main`}>
        {/* Header */}
        <Box className={classes.header}>
          <Box className={classes.headerLeft}>
            <Box className={classes.headerTitleRow}>
              {contentType === 'html' && (
                <img src={CrownIcon} alt="AI" className={classes.crownIcon} />
              )}
              <Typography className={classes.headerTitle}>
                {contentType === 'html' ? 'AI Referral Insights' : fileName || 'Document Viewer'}
              </Typography>
              {/* {contentType === 'html' && (
                <Typography className={classes.headerDisclaimer}>
                  (DISCLAIMER: AI-GENERATED CLINICAL SUPPORT TOOL FOR REFERENCE ONLY. NOT A MEDICAL RECORD.)
                </Typography>
              )} */}
            </Box>
            {/* {contentType === 'html' && (
              <Typography className={classes.headerNote}>
                NOTE: This summary is generated by Artificial Intelligence to assist with care coordination. It is not a comprehensive medical record, a physician's order, or a substitute for professional medical judgment. The Receiving Provider/Care Team is solely responsible for verifying all clinical data—including medications, allergies, and care instructions—against the official discharge documentation prior to initiating care. This software provider assumes no liability for clinical decisions made based on this text.
              </Typography>
            )} */}
          </Box>
          <IconButton className={classes.closeButton} onClick={onClose} aria-label="close" size="small">
            <CloseIcon style={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        {/* Content Area */}
        <Box className={`${classes.contentArea} content-viewer-inside-body`}>
          {this.renderContent()}
        </Box>
      </Box>
    );
  }
}

export default withStyles(styles)(ContentViewerPanel);
